import { useRef, useState } from "react";
import { useSignIn, useSignUp } from "@clerk/react";
import { isClerkAPIResponseError } from "@clerk/react/errors";
import { Screen } from "../components/Screen";
import { Button } from "../components/ui/button";
import { Field } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { copy } from "../lib/copy";

/**
 * Email, then a one-time code. Two fields, one at a time — three for a new
 * account, which is asked what to call it in between.
 *
 * Not Clerk's `<SignIn />`: it is a second design system and a second copy
 * source, in English, on the first screen anyone sees. This is the headless
 * API behind our own primitives, so the sign-in screen looks like the app and
 * every string in it lives in `copy.ts` with the rest.
 *
 * No password and no OAuth button. A household of four does not need passwords
 * to lose, and an OAuth redirect leaves a home-screen PWA for the browser and
 * comes back in the wrong place — see the Decisions table.
 *
 * Signing in and signing up are one flow here. Whether an address already has
 * an account is Clerk's business, not something the family should have to know
 * before they can type their email.
 *
 * The name step is where it is because of when a member row gets written. The
 * session going live is what triggers `bootstrap`, so anything asked after
 * `finalize()` arrives too late to name the row — it would already say
 * `nils@example.com`, and nothing in the app comes back to correct it. Asking
 * before `signUp.create` is what makes the row right the first time.
 */

/** Clerk's machine-stable codes. Messages are ours; `code` is the contract. */
const IDENTIFIER_NOT_FOUND = "form_identifier_not_found";

/**
 * The codes worth acting on are one level *down*.
 *
 * The Future API wraps an API failure in a `ClerkError` whose own `code` is the
 * generic `"api_response_error"`; the specific `form_*` codes live in
 * `errors[]` on the wrapper. Reading only the outer code matches nothing, ever
 * — which silently disables the sign-up fallback below, so a first-time user
 * gets "det gick inte just nu" instead of an account. Found by driving the real
 * flow; it typechecks either way.
 */
function codesOf(error: { code: string }): string[] {
  return isClerkAPIResponseError(error) ? error.errors.map((e) => e.code) : [error.code];
}

/**
 * Clerk's bot-protection challenge can leave a call pending forever rather than
 * rejecting — `signUp.create` does exactly this when the challenge never
 * resolves. Left alone that is the worst failure this screen has: the button
 * stays disabled, nothing is said, and the only way out is a reload.
 *
 * A timeout turns it into the error state the screen already has, which is what
 * "errors say what happened and what to do" requires.
 */
const CLERK_TIMEOUT_MS = 20000;

function withTimeout<T>(work: Promise<T>): Promise<T> {
  return Promise.race([
    work,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("clerk timeout")), CLERK_TIMEOUT_MS),
    ),
  ]);
}

function messageFor(error: { code: string }) {
  for (const code of codesOf(error)) {
    switch (code) {
      case IDENTIFIER_NOT_FOUND:
      case "form_param_format_invalid":
      case "form_param_nil":
      case "form_identifier_exists":
        return copy.auth.errors.email;
      case "form_code_incorrect":
      case "verification_failed":
        return copy.auth.errors.code;
      case "verification_expired":
      case "form_code_expired":
        return copy.auth.errors.expired;
      case "too_many_requests":
      case "verification_rate_limit_exceeded":
        return copy.auth.errors.tooMany;
    }
  }
  return copy.auth.errors.generic;
}

export function SignInScreen() {
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();

  const [step, setStep] = useState<"email" | "name" | "code">("email");
  // Which resource holds the attempt. Set when the code is sent, because that
  // is the call that discovers whether the address has an account.
  const [mode, setMode] = useState<"signIn" | "signUp">("signIn");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  async function sendCode() {
    const address = email.trim();
    if (!address || pending) return;

    setPending(true);
    setError(null);
    try {
      const attempt = await withTimeout(signIn.emailCode.sendCode({ emailAddress: address }));

      if (!attempt.error) {
        setMode("signIn");
        setStep("code");
        return;
      }

      // Anything but "no such account" is a real failure. Only that one falls
      // through to creating the account.
      if (!codesOf(attempt.error).includes(IDENTIFIER_NOT_FOUND)) {
        setError(messageFor(attempt.error));
        return;
      }

      // Nobody has this address, so this is a sign-up. Nothing is created and
      // no code is sent yet: `createAccount` does both, once there is a name to
      // create the account with.
      setStep("name");
    } catch {
      // A timeout, or a Clerk call that rejected rather than returning an
      // error. Without this the rejection escapes as an unhandled promise and
      // the screen just sits there looking like nothing was pressed.
      setError(copy.auth.errors.generic);
    } finally {
      setPending(false);
    }
  }

  /**
   * Create the account, with the name on it from the start.
   *
   * `firstName` is only accepted by an instance with **First and last name**
   * enabled in its user settings. Turned off, Clerk rejects the call and the
   * screen shows its generic error — a configuration failure wearing a user
   * error's clothes, which is why it is on the slice 7 checklist in ROADMAP.md.
   */
  async function createAccount() {
    const value = name.trim();
    if (!value || pending) return;

    setPending(true);
    setError(null);
    try {
      const created = await withTimeout(
        signUp.create({ emailAddress: email.trim(), firstName: value }),
      );
      if (created.error) {
        setError(messageFor(created.error));
        return;
      }

      const sent = await withTimeout(signUp.verifications.sendEmailCode());
      if (sent.error) {
        setError(messageFor(sent.error));
        return;
      }

      setMode("signUp");
      setStep("code");
    } catch {
      setError(copy.auth.errors.generic);
    } finally {
      setPending(false);
    }
  }

  async function verify() {
    const value = code.trim();
    if (!value || pending) return;

    setPending(true);
    setError(null);
    try {
      // `finalize` is what makes the new session the active one. Without it the
      // code verifies and nothing happens, which is the easiest way to get this
      // flow subtly wrong.
      const attempt =
        mode === "signIn"
          ? await withTimeout(signIn.emailCode.verifyCode({ code: value }))
          : await withTimeout(signUp.verifications.verifyEmailCode({ code: value }));

      if (attempt.error) {
        setError(messageFor(attempt.error));
        return;
      }

      const done = mode === "signIn" ? await signIn.finalize() : await signUp.finalize();
      if (done.error) setError(messageFor(done.error));
    } catch {
      setError(copy.auth.errors.generic);
    } finally {
      setPending(false);
    }
  }

  async function resend() {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const sent =
        mode === "signIn"
          ? await withTimeout(signIn.emailCode.sendCode({ emailAddress: email.trim() }))
          : await withTimeout(signUp.verifications.sendEmailCode());
      if (sent.error) setError(messageFor(sent.error));
    } catch {
      setError(copy.auth.errors.generic);
    } finally {
      setPending(false);
    }
  }

  /** Back to the first field, with the attempt thrown away rather than reused. */
  function changeEmail() {
    void signIn.reset();
    void signUp.reset();
    setStep("email");
    setName("");
    setCode("");
    setError(null);
  }

  if (step === "email") {
    return (
      <Screen
        title={copy.auth.title}
        tabBar={false}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void sendCode();
          }}
        >
          <p className="mb-6 text-body text-muted">{copy.auth.intro}</p>

          <Field label={copy.auth.email} variant="plain">
            <Input
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.auth.emailPlaceholder}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              enterKeyHint="send"
            />
          </Field>

          {error && <p className="mt-3 text-meta text-alert">{error}</p>}

          {/* Directly under the field, not docked: there is no tab bar here
              and the action is this form's submit, so it belongs to the form.
              Being the real submit is also what the keyboard's send key uses. */}
          <Button type="submit" block disabled={!email.trim() || pending} className="mt-6">
            {copy.auth.sendCode}
          </Button>
        </form>
      </Screen>
    );
  }

  /**
   * Only a new account reaches this. The button keeps the name the first screen
   * gave it — pressing it is what finally sends the code, so "Skicka kod" is
   * still what it does.
   */
  if (step === "name") {
    return (
      <Screen
        title={copy.auth.nameTitle}
        tabBar={false}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void createAccount();
          }}
        >
          <p className="mb-6 text-body text-muted">{copy.auth.nameIntro}</p>

          <Field label={copy.auth.name} variant="plain">
            <Input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={copy.auth.namePlaceholder}
              // A first name, so `given-name` rather than `name` — it is what
              // iOS offers from the contact card.
              autoComplete="given-name"
              autoCapitalize="words"
              autoCorrect="off"
              enterKeyHint="send"
              // Mirrors MAX_NAME_LENGTH in households.ts, which clamps anyway.
              // Stopping it at the keyboard is the half that can be seen.
              maxLength={32}
            />
          </Field>

          {error && <p className="mt-3 text-meta text-alert">{error}</p>}

          {/*
            Clerk renders its bot-protection widget into this element when the
            instance has bot protection on. A custom flow has no widget of its
            own for it to attach to, so without this element a first-time
            sign-up fails and the reason is invisible.

            It lives on this step and not the first one because `signUp.create`
            is what the widget guards, and that call moved here with the name.

            Not a styled element and not a candidate for a primitive — it is a
            mount point, empty in the common case, since smart CAPTCHA is
            invisible unless it decides otherwise.
          */}
          <div id="clerk-captcha" />

          <Button type="submit" block disabled={!name.trim() || pending} className="mt-6">
            {copy.auth.sendCode}
          </Button>

          <Button variant="quiet" onClick={changeEmail} className="mt-4">
            {copy.auth.changeEmail}
          </Button>
        </form>
      </Screen>
    );
  }

  return (
    <Screen
      title={copy.auth.codeTitle}
      tabBar={false}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void verify();
        }}
      >
        <p className="mb-6 text-body text-muted">
          {copy.auth.codeSentTo} {email.trim()}
        </p>

        <Field label={copy.auth.code} variant="plain">
          <Input
            ref={codeRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={copy.auth.codePlaceholder}
            // `one-time-code` is what lets iOS offer the code from the mail
            // notification above the keyboard, which is the whole ergonomics
            // of this screen on a phone.
            autoComplete="one-time-code"
            inputMode="numeric"
            enterKeyHint="done"
          />
        </Field>

        {error && <p className="mt-3 text-meta text-alert">{error}</p>}

        <Button type="submit" block disabled={!code.trim() || pending} className="mt-6">
          {copy.auth.signIn}
        </Button>

        <div className="mt-4 flex flex-col items-start">
          <Button variant="quiet" disabled={pending} onClick={() => void resend()}>
            {copy.auth.resend}
          </Button>
          <Button variant="quiet" onClick={changeEmail}>
            {copy.auth.changeEmail}
          </Button>
        </div>
      </form>
    </Screen>
  );
}
